import { rem } from 'polished'
import { useCallback, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import IconButton from 'LuxKit/Button/IconButton'
import LineMinusIcon from 'LuxKit/Icons/line/LineMinusIcon'
import SolidPlusIcon from 'LuxKit/Icons/solid/SolidPlusIcon'
import Image from 'components/Common/Image'
import useLocallyPersistedState from 'hooks/useLocallyPersistedState'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { useScreenSizeBetween } from 'hooks/useScreenSize'
import { mediaQueryUp } from 'components/utils/breakpoint'

const MAP_VIEW_OPTION_IMAGE_IDS = {
  ROADMAP: '595e97ee-0807-4871-828f-ba7bb055e82d',
  HYBRID: '3cb07d78-8dbe-41b8-b8ab-962f8040cbd3', // Satellite view
}

const ZoomButtonContainer = styled.div`
  position: absolute;
  right: ${rem(10)};
  bottom: ${rem(76)};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  border-radius: ${rem(2)};

  @media (max-width: 369px) {
    right: ${rem(16)};
    bottom: ${rem(96)};
  }

  ${mediaQueryUp.desktop} {
    right: ${rem(20)};
    top: ${rem(90)};
    bottom: unset;
  }
`

const ShadowedIconButton = styled(IconButton)`
  background-color: white !important;
  box-shadow: ${(props) => props.theme.shadow.flat.small};
`

interface Props {
  mapRef: React.MutableRefObject<google.maps.Map | undefined>
}

const MAX_ZOOM = 20
const MIN_ZOOM = 2

function MapControlButtons(props: Props) {
  const { mapRef } = props
  const intl = useIntl()
  const isTabletOrSmaller = useScreenSizeBetween('mobile', 'tablet')
  const tripId = useTripId()
  const [isSatelliteView, setIsSatelliteView] = useLocallyPersistedState<boolean>({
    defaultValue: false,
    key: 'tripPlannerMapSatelliteView-' + tripId,
    version: 1,
    returnDefaultBeforeRestoring: true,
  })

  const currentZoom = mapRef.current?.getZoom()
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setMapTypeId(isSatelliteView ? google.maps.MapTypeId.HYBRID : google.maps.MapTypeId.ROADMAP)
    }
  }, [mapRef, isSatelliteView])

  const zoomIn = () => {
    if (currentZoom) {
      mapRef.current?.setZoom(currentZoom + 1)
    }
  }

  const zoomOut = () => {
    if (currentZoom) {
      mapRef.current?.setZoom(currentZoom - 1)
    }
  }

  const toggleSatelliteView = useCallback(() => {
    setIsSatelliteView(prev => !prev)
  }, [setIsSatelliteView])

  return (
    <ZoomButtonContainer>
      {!isTabletOrSmaller && <>
        <ShadowedIconButton
          aria-label={intl.formatMessage({
            id: 'jqlaGdmCbJhqs-1_gBxm',
            defaultMessage: 'Zoom in',
            description: 'Accessibility label for the map zoom in button in the trip planner map view',
          })}
          title={intl.formatMessage({
            id: 'mboBKDycoW2asRJ_Lqno',
            defaultMessage: 'Zoom in',
            description: 'Tooltip for the map zoom in button on the map or location view',
          })}
          kind="tertiary"
          disabled={currentZoom === MAX_ZOOM}
          onClick={zoomIn}
        >
          <SolidPlusIcon />
        </ShadowedIconButton>
        <ShadowedIconButton
          aria-label={intl.formatMessage({
            id: '1NkjmEIRD-GPVX9c6IsH',
            defaultMessage: 'Zoom out',
            description: 'Accessibility label for the map zoom out button in the trip planner map view',
          })}
          title={intl.formatMessage({
            id: '7uM0SaBR8lP9QCnXQtqI',
            defaultMessage: 'Zoom out',
            description: 'Tooltip for the map zoom out button on the map or location view',
          })}
          kind="tertiary"
          disabled={currentZoom === MIN_ZOOM}
          onClick={zoomOut}
        >
          <LineMinusIcon />
        </ShadowedIconButton>
      </>}
      <ShadowedIconButton
        aria-label={intl.formatMessage({
          id: 'IN9Z4vOnttMCeA_PO77b',
          defaultMessage: 'Satellite view',
          description: 'Accessibility label for the satellite view toggle button',
        })}
        title={intl.formatMessage({
          id: 'MA2Ifr-F8_GI5WLTlzEq',
          defaultMessage: 'Satellite view',
          description: 'Tooltip for the satellite view toggle button in the trip planner map view',
        })}
        kind="tertiary"
        onClick={toggleSatelliteView}
      >
        <Image image={{ id: MAP_VIEW_OPTION_IMAGE_IDS[isSatelliteView ? 'ROADMAP' : 'HYBRID'] }} fit="center" />
      </ShadowedIconButton>
    </ZoomButtonContainer>
  )
}

export default MapControlButtons
